import type { Tone } from "@/pages/admin/components/AdminUI";

export interface StatusMeta {
  label: string;
  tone: Tone;
}

const meta = (label: string, tone: Tone): StatusMeta => ({ label, tone });

/* ---------------------------------- Farmers --------------------------------- */
export interface Farmer {
  id: number;
  name: string;
  slug: string | null;
  village: string | null;
  district: string | null;
  crop: string | null;
  farming_for: string | null;
  phone: string | null;
  email: string | null;
  quote: string | null;
  intro: string | null;
  bio: string[] | null;
  specialties: string[] | null;
  status: string;
  verification: string;
  training_status: string;
  rating: number;
  total_bookings: number;
  total_revenue: number;
  image: string | null;
  created_at: string;
  updated_at: string;
}

export const FARMER_STATUS: Record<string, StatusMeta> = {
  application: meta("Application", "accent"),
  under_review: meta("Under Review", "accent"),
  verification: meta("Verification", "accent"),
  training: meta("Training", "secondary"),
  pilot: meta("Pilot", "secondary"),
  approved: meta("Approved", "primary"),
  active: meta("Active", "primary"),
  suspended: meta("Suspended", "neutral"),
};

export const VERIFICATION_STATUS: Record<string, StatusMeta> = {
  pending: meta("Pending", "accent"),
  verified: meta("Verified", "primary"),
  rejected: meta("Rejected", "neutral"),
};

export const TRAINING_STATUS: Record<string, StatusMeta> = {
  not_started: meta("Not Started", "neutral"),
  scheduled: meta("Scheduled", "accent"),
  in_progress: meta("In Progress", "secondary"),
  completed: meta("Completed", "primary"),
  expired: meta("Expired", "neutral"),
};

/* ----------------------------------- Farms ---------------------------------- */
export interface Farm {
  id: number;
  name: string;
  slug: string | null;
  farmer_name: string | null;
  farmer_id: number | null;
  village: string | null;
  district: string | null;
  crop: string | null;
  area: string | null;
  established: string | null;
  summary: string | null;
  highlights: string[] | null;
  types: string[] | null;
  capacity: number;
  rating: number;
  status: string;
  verification: string;
  safety: string;
  image: string | null;
  map_query: string | null;
  experience_slug: string | null;
  created_at: string;
  updated_at: string;
}

export const FARM_STATUS: Record<string, StatusMeta> = {
  draft: meta("Draft", "neutral"),
  pending_approval: meta("Pending Approval", "accent"),
  verified: meta("Verified", "primary"),
  active: meta("Active", "primary"),
  temporarily_closed: meta("Temporarily Closed", "accent"),
  suspended: meta("Suspended", "neutral"),
  archived: meta("Archived", "neutral"),
};

export const SAFETY_STATUS: Record<string, StatusMeta> = {
  pending: meta("Pending", "neutral"),
  due: meta("Due", "accent"),
  passed: meta("Passed", "primary"),
  corrective: meta("Corrective", "accent"),
  overdue: meta("Overdue", "neutral"),
};

/* ------------------------------ Applications -------------------------------- */
export interface FarmerApplication {
  id: number;
  farmer_name: string;
  mobile: string | null;
  email: string | null;
  village: string | null;
  district: string | null;
  farm_name: string | null;
  farm_size: string | null;
  farm_type: string | null;
  crops: string | null;
  facilities: string | null;
  experience_ideas: string | null;
  bank_details: string | null;
  stage: string;
  score: number;
  assigned_officer: string | null;
  notes: string | null;
  submitted_at: string | null;
  created_at: string;
  updated_at: string;
}

export const APPLICATION_STAGE: Record<string, StatusMeta> = {
  submitted: meta("Submitted", "accent"),
  screening: meta("Initial Screening", "accent"),
  field_verification: meta("Field Verification", "secondary"),
  documents_review: meta("Documents Review", "secondary"),
  training: meta("Training", "secondary"),
  pilot: meta("Pilot", "secondary"),
  approved: meta("Approved", "primary"),
  rejected: meta("Rejected", "neutral"),
};

/* ------------------------------ Verifications ------------------------------- */
export interface FarmVerification {
  id: number;
  farm_name: string;
  status: string;
  safety_score: number;
  experience_score: number;
  hygiene_score: number;
  accessibility_score: number;
  host_score: number;
  facilities_score: number;
  uniqueness_score: number;
  environment_score: number;
  total_score: number;
  outcome: string;
  critical_issue: boolean;
  notes: string | null;
  inspected_at: string | null;
  next_audit: string | null;
  created_at: string;
  updated_at: string;
}

export const VERIFICATION_OUTCOME: Record<string, StatusMeta> = {
  pilot_eligible: meta("Pilot Eligible", "primary"),
  improvement: meta("Improvement Required", "accent"),
  hold: meta("Hold", "neutral"),
};

export const VERIFICATION_WORKFLOW: Record<string, StatusMeta> = {
  pending: meta("Pending", "neutral"),
  scheduled: meta("Scheduled", "accent"),
  in_progress: meta("In Progress", "secondary"),
  passed: meta("Passed", "primary"),
  improvement: meta("Improvement", "accent"),
  hold: meta("Hold", "neutral"),
};

export const VERIFICATION_CATEGORIES: { key: string; label: string; max: number }[] = [
  { key: "safety_score", label: "Safety", max: 25 },
  { key: "experience_score", label: "Experience", max: 20 },
  { key: "hygiene_score", label: "Hygiene", max: 15 },
  { key: "accessibility_score", label: "Accessibility", max: 10 },
  { key: "host_score", label: "Host Capability", max: 10 },
  { key: "facilities_score", label: "Facilities", max: 10 },
  { key: "uniqueness_score", label: "Uniqueness", max: 5 },
  { key: "environment_score", label: "Environment", max: 5 },
];

/* -------------------------------- Training ---------------------------------- */
export interface TrainingRecord {
  id: number;
  farmer_name: string;
  module: string;
  trainer: string | null;
  status: string;
  scheduled_date: string | null;
  score: number;
  certificate: string | null;
  expires_at: string | null;
  created_at: string;
  updated_at: string;
}

export const TRAINING_MODULES = [
  "Hospitality",
  "Visitor Communication",
  "Storytelling",
  "Safety",
  "First Aid",
  "Food Hygiene",
  "Animal Safety",
  "Booking Operations",
  "Emergency Response",
];

/* ------------------------------ Safety Audits ------------------------------- */
export interface SafetyAudit {
  id: number;
  farm_name: string;
  last_audit: string | null;
  next_audit: string | null;
  score: number;
  critical_issues: number;
  status: string;
  checklist: Record<string, string> | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export const AUDIT_STATUS: Record<string, StatusMeta> = {
  passed: meta("Passed", "primary"),
  due: meta("Due", "accent"),
  overdue: meta("Overdue", "accent"),
  in_progress: meta("In Progress", "secondary"),
  corrective: meta("Corrective Action", "accent"),
};

export const AUDIT_CHECKLIST: { key: string; label: string }[] = [
  { key: "entry", label: "Entry" },
  { key: "parking", label: "Parking" },
  { key: "paths", label: "Paths" },
  { key: "water", label: "Water" },
  { key: "electricity", label: "Electricity" },
  { key: "machinery", label: "Machinery" },
  { key: "animals", label: "Animals" },
  { key: "chemicals", label: "Chemicals" },
  { key: "food", label: "Food" },
  { key: "toilets", label: "Toilets" },
  { key: "first_aid", label: "First Aid" },
  { key: "emergency_access", label: "Emergency Access" },
];

/* ------------------------------ Settlements --------------------------------- */
export interface FarmerSettlement {
  id: number;
  farmer_name: string;
  farm_name: string | null;
  period: string | null;
  gross_booking: number;
  commission: number;
  operational_costs: number;
  refund_adjustments: number;
  farmer_share: number;
  final_settlement: number;
  status: string;
  created_at: string;
  updated_at: string;
}

export const SETTLEMENT_STATUS: Record<string, StatusMeta> = {
  pending: meta("Pending", "accent"),
  ready: meta("Ready", "secondary"),
  processing: meta("Processing", "secondary"),
  paid: meta("Paid", "primary"),
  failed: meta("Failed", "neutral"),
};

/* --------------------------------- Visitors --------------------------------- */
export interface Visitor {
  id: number;
  name: string;
  phone: string | null;
  email: string | null;
  visitor_type: string;
  location: string | null;
  total_bookings: number;
  total_visits: number;
  total_spend: number;
  last_visit: string | null;
  status: string;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export const VISITOR_TYPE: Record<string, StatusMeta> = {
  individual: meta("Individual", "secondary"),
  family: meta("Family", "primary"),
  school: meta("School", "accent"),
  college: meta("College", "accent"),
  corporate: meta("Corporate", "primary"),
  tourist: meta("Tourist", "secondary"),
  group: meta("Group", "secondary"),
};

export const VISITOR_STATUS: Record<string, StatusMeta> = {
  active: meta("Active", "primary"),
  blocked: meta("Blocked", "neutral"),
};

/* --------------------------------- Reviews ---------------------------------- */
export interface Review {
  id: number;
  visitor_name: string;
  farm_name: string | null;
  experience: string | null;
  rating: number;
  comment: string | null;
  visit_date: string | null;
  status: string;
  categories: Record<string, number> | null;
  photos: { src: string; alt?: string }[] | null;
  role: string | null;
  location: string | null;
  village: string | null;
  avatar: string | null;
  experience_slug: string | null;
  created_at: string;
  updated_at: string;
}

export const REVIEW_STATUS: Record<string, StatusMeta> = {
  published: meta("Published", "primary"),
  pending: meta("Pending", "accent"),
  hidden: meta("Hidden", "neutral"),
};

export const REVIEW_CATEGORIES: { key: string; label: string }[] = [
  { key: "overall", label: "Overall" },
  { key: "farm", label: "Farm" },
  { key: "experience", label: "Experience" },
  { key: "host", label: "Host" },
  { key: "food", label: "Food" },
  { key: "cleanliness", label: "Cleanliness" },
  { key: "safety", label: "Safety" },
];

/* ---------------------------------- helper ---------------------------------- */
export function metaFor(map: Record<string, StatusMeta>, key: string | null | undefined): StatusMeta {
  if (!key) return { label: "—", tone: "neutral" };
  return map[key] || { label: key, tone: "neutral" };
}

export function optionsFrom(map: Record<string, StatusMeta>): { value: string; label: string }[] {
  return Object.entries(map).map(([value, m]) => ({ value, label: m.label }));
}